import React from 'react'
import moment from 'moment'

const LOCKED_STATUSES = new Set(['S', 'L', 'ML'])

export const EventCellRenderer = ({ value, data, colDef }) => {
  if (value == null || value === '') return null
  const field = colDef && colDef.field
  const hNum =
    field && field.startsWith('h') ? parseInt(field.slice(1), 10) : NaN
  const arr =
    !isNaN(hNum) && data && data.eventDataMap && data.eventDataMap[hNum]
  const count = Array.isArray(arr) ? arr.length : 0
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {count > 0 && (
        <span
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            minWidth: 14,
            height: 14,
            borderRadius: '0 0 0 4px',
            backgroundColor: '#ffd591',
            color: '#873800',
            fontSize: 9,
            fontWeight: 700,
            lineHeight: '14px',
            padding: '0 3px',
            textAlign: 'center',
          }}
        >
          {count}
        </span>
      )}
      <span style={{ position: 'absolute', bottom: 2, left: 4, fontSize: 12 }}>
        {field && data && data[`${field}_w`] != null ? (
          <>
            <span style={{ display: 'block', lineHeight: '13px' }}>
              {Number(value).toFixed(1)}
            </span>
            <span
              style={{ display: 'block', lineHeight: '13px', color: '#595959' }}
            >
              ({Number(data[`${field}_w`]).toFixed(1)})
            </span>
          </>
        ) : (
          Number(value).toFixed(1)
        )}
      </span>
    </div>
  )
}

export const HourCellRenderer = ({ colDef, context }) => {
  if (!context || !context.hasWrite) return null
  const field = colDef && colDef.field
  if (!field || !field.startsWith('h') || field === 'h2a') return null
  const hNum = parseInt(field.slice(1), 10)
  if (isNaN(hNum)) return null
  const statusRow = context && context.statusRow
  const hourStatus = statusRow && statusRow[`h${hNum}`]
  if (LOCKED_STATUSES.has(hourStatus)) return null
  return (
    <span
      style={{
        cursor: 'pointer',
        color: '#1890ff',
        fontWeight: 'bold',
        fontSize: 16,
      }}
      onClick={() => context && context.onAddAtHour(hNum)}
    >
      +
    </span>
  )
}

export const UpdateCellRenderer = ({ data, context }) => {
  if (!data || data.rowType !== 'event') return null
  if (!context || !context.hasWrite) return null
  return (
    <span
      style={{ cursor: 'pointer', color: '#1890ff', fontSize: 12 }}
      onClick={() => context && context.onUpdateEvent(data)}
    >
      Update
    </span>
  )
}

export const DeleteCellRenderer = ({ data, context }) => {
  if (!data || data.rowType !== 'event') return null
  if (!context || !context.hasWrite) return null
  return (
    <span
      style={{ cursor: 'pointer', color: '#ff4d4f', fontSize: 12 }}
      onClick={() => context && context.onDeleteEvent(data)}
    >
      Delete
    </span>
  )
}

export const MultiLineTooltip = ({ value }) => {
  if (!value) return null
  const lines = String(value).split('\n')
  const isTable = lines.some(l => l.includes('\t'))
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        padding: '6px 10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontSize: 12,
        lineHeight: '20px',
        whiteSpace: 'nowrap',
      }}
    >
      {isTable ? (
        <table style={{ borderCollapse: 'collapse', minWidth: 160 }}>
          <tbody>
            {lines.map((line, i) => {
              const cells = line.split('\t')
              const isHeader = i === 0
              return (
                <tr
                  key={i}
                  style={
                    isHeader ? { borderBottom: '1px solid #d9d9d9' } : undefined
                  }
                >
                  {cells.map((cell, j) => (
                    <td
                      key={j}
                      style={{
                        padding: isHeader ? '2px 10px 4px' : '2px 10px',
                        fontWeight: isHeader ? 700 : 400,
                        textAlign: j > 0 ? 'right' : 'left',
                        color: isHeader ? '#8c8c8c' : '#262626',
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : (
        lines.map((line, i) => <div key={i}>{line}</div>)
      )}
    </div>
  )
}

// Builds the sub-hour breakdown tooltip string for event row cells
export const buildEventTooltip = events => {
  if (!events || !events.length) return null
  const sorted = events
    .slice()
    .sort(
      (a, b) => moment(a.eventStart).minute() - moment(b.eventStart).minute()
    )
  const lines = sorted.map((e, i) => {
    const minStart = moment(e.eventStart).minute()
    const minEnd =
      i < sorted.length - 1 ? moment(sorted[i + 1].eventStart).minute() : 60
    const endStr = moment(e.eventStart)
      .startOf('hour')
      .add(minEnd, 'minutes')
      .format('HH:mm')
    const cap = e.availCap != null ? Number(e.availCap) : null
    const weighted = cap != null ? cap * ((minEnd - minStart) / 60) : null
    const capStr = cap != null ? cap.toFixed(1) : '-'
    const weightedStr = weighted != null ? ` (w: ${weighted.toFixed(1)})` : ''
    return {
      line: `${moment(e.eventStart).format(
        'HH:mm'
      )}–${endStr} → ${capStr}${weightedStr}`,
      weighted,
    }
  })
  const totalWeighted = lines.reduce(
    (s, { weighted }) => s + (weighted != null ? weighted : 0),
    0
  )
  return [
    ...lines.map(l => l.line),
    '─────────────────',
    `Total (weighted): ${totalWeighted.toFixed(1)}`,
  ].join('\n')
}
